import React from 'react';
import {useTranslation} from 'react-i18next';

import Radio from '../Radio';
import {SectionWithBorder} from '../SectionWithBorder';
import {Stack} from '../primitives';
import {Image} from './Image';
import {ImageActions, UrlActions} from './ImageActions';
import {
  EImagePickerError,
  ELoadType,
  TContext,
  TCustomValidator,
  TImagePickerResult,
  TRestrictions,
} from './definitions';
import * as S from './style';
import {getImageInfo, handleUpload, requestFile, showErrors} from './utils';

type TProps = {
  name?: string;
  isInvalid?: boolean;
  readOnly?: boolean;
  isMultipleFiles?: boolean;
  dataSelector?: string;
  restrictions?: TRestrictions;
  onBlur?: TEmptyFunction;
  onChange?: (result: TImagePickerResult[]) => void;
  onError?: (error: EImagePickerError[]) => void;
  onDelete?: TEmptyFunction;
  isImageOnly?: boolean;
  width?: string;
  hasDeleteButton?: boolean;
  value?: string;
  error?: string;
  isShowErrors?: boolean;
  hasCropper?: boolean;
  customValidator?: TCustomValidator;
  fileName?: string;
  uploadImageByUrl?: (url: string) => Promise<string | void>;
};

const ImagePicker = React.memo<TProps>(
  ({
    name,
    isInvalid,
    readOnly,
    isMultipleFiles,
    dataSelector,
    onChange,
    onError,
    onDelete,
    onBlur,
    restrictions = {},
    isImageOnly,
    width = '564px',
    hasDeleteButton,
    value,
    error,
    hasCropper,
    isShowErrors,
    customValidator,
    uploadImageByUrl,
    fileName,
  }) => {
    const {t} = useTranslation();
    const inputRef = React.useRef<HTMLInputElement>(null);
    const urlRef = React.useRef<HTMLInputElement>(null);
    const [loadType, setLoadType] = React.useState<ELoadType>(ELoadType.IMAGE);
    const [context, setContext] = React.useState<TContext>({});

    const updateContext = (data: TContext) =>
      setContext((prevContext) => ({...prevContext, ...data}));

    React.useEffect(() => {
      updateContext({
        imageInfo: null,
        isLoading: !!value,
        urlErrorKey: null,
      });

      if (value) {
        (async () => {
          const info = await getImageInfo(value);

          setLoadType(ELoadType.IMAGE);
          updateContext({
            imageInfo: info,
            isLoading: false,
          });
        })();
      }
    }, [value]);

    const handleChange = React.useCallback(
      async (imageValue: File[] | string) => {
        let files: File[] = [];

        updateContext({isLoading: true});

        if (typeof imageValue === 'string') {
          const fileUrl = await uploadImageByUrl?.(imageValue);

          if (!fileUrl) {
            updateContext({
              urlErrorKey: 'EdnaImagePicker:loadingByUrlError',
              isLoading: false,
            });

            onError?.([EImagePickerError.URL]);

            return;
          }

          const {file} = await requestFile(fileUrl);

          if (file) {
            files.push(file);
          }
        } else {
          files = imageValue;
        }

        if (!files.length) {
          return;
        }

        const {successUploads, errorUploads} = await handleUpload({
          files,
          restrictions,
          hasCropper,
          customValidator,
        });

        if (isShowErrors && errorUploads.length) {
          showErrors(errorUploads, restrictions, t);
        }

        if (errorUploads.length) {
          onError?.(errorUploads);
          updateContext({imageInfo: null});
        }

        if (successUploads.length) {
          onChange?.(successUploads);
        }

        updateContext({isLoading: false, urlErrorKey: null});

        if (urlRef.current?.value) {
          urlRef.current.value = '';
        }
      },
      [updateContext, onError, onChange, isShowErrors, hasCropper, restrictions, t],
    );

    const handleTypeChange = (event: React.ChangeEvent<HTMLInputElement>) =>
      setLoadType(event as unknown as ELoadType);
    const handleEdit = () => inputRef.current?.click();

    const showControls =
      !context.isLoading && !context.imageInfo && !isImageOnly && loadType !== ELoadType.URL;
    const isImageReadOnly =
      context.isLoading || !!context.imageInfo || readOnly || loadType === ELoadType.URL;

    return (
      <SectionWithBorder
        readOnly={readOnly}
        hasError={!context.isLoading && (isInvalid || !!context.urlErrorKey)}
        width={width}
      >
        <S.Wrapper data-selector={dataSelector}>
          <Image
            context={context}
            onChange={handleChange}
            fieldRef={inputRef}
            restrictions={restrictions}
            onBlur={onBlur}
            readOnly={isImageReadOnly}
            name={name}
            isInvalid={loadType === ELoadType.IMAGE && !context.isLoading ? isInvalid : false}
            isMultiple={isMultipleFiles}
            error={error}
            isShowError={!isImageOnly && loadType === ELoadType.IMAGE}
          />
          {showControls && (
            <Stack size="2">
              <Radio
                name={`${name}.loadType`}
                type="radio"
                value={ELoadType.IMAGE}
                readOnly={readOnly}
                checked={loadType === ELoadType.IMAGE}
                onChange={handleTypeChange}
              >
                {t('EdnaImagePicker:uploadImage')}
              </Radio>
              <Radio
                name={`${name}.loadType`}
                type="radio"
                value={ELoadType.URL}
                readOnly={readOnly}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                checked={loadType === ELoadType.URL}
                onChange={handleTypeChange}
              >
                {t('EdnaImagePicker:uploadByUrl')}
              </Radio>
            </Stack>
          )}
          {loadType === ELoadType.IMAGE && (
            <ImageActions
              fileName={fileName}
              context={context}
              onEdit={handleEdit}
              isInvalid={isInvalid}
              readOnly={readOnly}
              error={error}
              isImageOnly={isImageOnly}
              onDelete={hasDeleteButton ? onDelete : undefined}
            />
          )}
          {loadType === ELoadType.URL && (
            <UrlActions
              inputRef={urlRef}
              context={context}
              updateContext={updateContext}
              isInvalid={isInvalid}
              readOnly={readOnly}
              error={error}
              name={name}
              onBlur={onBlur}
              onChange={handleChange}
              onDelete={hasDeleteButton ? onDelete : undefined}
            />
          )}
        </S.Wrapper>
      </SectionWithBorder>
    );
  },
);

ImagePicker.displayName = 'ImagePicker';

export {ImagePicker};
export type {TProps};
